import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { InventoryAdjustmentReason } from '../../../services/inventory/types';
import { Modal, ModalHeader, ModalBody } from '../../../components/library/Modal';
import { Button } from '../../../components/library';
import axios from '../../../utils/axios';
import { API_URL } from '../../../constants';
import styled from 'styled-components';

const ModalFooter = styled(Box)`
  display: flex;
  justify-content: flex-end;
  padding: 16px 0;
  gap: 8px;
  border-top: 1px solid ${props => props.theme.palette.grey[200]};
  margin-top: 24px;
`;

const ConfigGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

interface Permission {
  id: number;
  name: string;
}

interface AdjustmentReasonModalProps {
  toggleModal: () => void;
  formSuccess: (formState: Record<string, any>) => void;
  error?: boolean;
  loading?: boolean;
  selectedReason?: InventoryAdjustmentReason | null;
}

const AdjustmentReasonModal: FC<AdjustmentReasonModalProps> = ({
  toggleModal,
  formSuccess,
  error,
  loading,
  selectedReason,
}) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [formState, setFormState] = useState<Record<string, any>>({});

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await axios.get(`${API_URL}/admin/users/permissions`);
        setPermissions((response as any).data || []);
      } catch (err) {
        console.error('Error fetching permissions:', err);
      }
    };
    fetchPermissions();
  }, []);

  useEffect(() => {
    const config = selectedReason?.config || {};
    const netsuiteAccount = config.netsuiteAccount ?? null;
    const initialValues: Record<string, any> = {
      code: selectedReason?.code || '',
      description: selectedReason?.description || '',
      active: selectedReason?.active ?? true,
      exportToNetsuite: config.exportToNetsuite ?? selectedReason?.exportToNetsuite ?? true,
      exportToShopify: config.exportToShopify ?? selectedReason?.exportToShopify ?? true,
      netsuiteAccountId: netsuiteAccount?.accountId?.toString() || '',
      netsuiteAccountName: netsuiteAccount?.accountName || '',
      amountEligible: config.amountEligible ?? selectedReason?.amountEligible ?? false,
      allowInventoryCreate: config.allowInventoryCreate ?? false,
      quantityAdjustmentDirection: config.quantityAdjustmentDirection ?? 'both',
      permissions: selectedReason?.permissionList || [],
    };

    setFormState(initialValues);
  }, [selectedReason]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    formSuccess(formState);
  };

  const handleChange = (field: string, value: any) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field, e.target.checked);
  };

  return (
    <Modal open={true} handleClose={toggleModal} label="Adjustment Reason" maxWidth={800}>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <ModalHeader>
          {selectedReason ? 'Edit Adjustment Reason' : 'Create Adjustment Reason'}
        </ModalHeader>
        <ModalBody>
          <TextField
            fullWidth
            variant="outlined"
            label="Code"
            value={formState.code || ''}
            onChange={e => handleChange('code', e.target.value)}
            required
            margin="normal"
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Description"
            value={formState.description || ''}
            onChange={e => handleChange('description', e.target.value)}
            required
            margin="normal"
            multiline
            rows={2}
          />

          <Box mt={3} mb={2}>
            <Typography variant="h6" gutterBottom>
              Configuration
            </Typography>
            <ConfigGrid>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formState.active ?? true}
                      onChange={handleCheckboxChange('active')}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formState.exportToNetsuite ?? true}
                      onChange={handleCheckboxChange('exportToNetsuite')}
                    />
                  }
                  label="Export to NetSuite"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formState.amountEligible ?? false}
                      onChange={handleCheckboxChange('amountEligible')}
                    />
                  }
                  label="Amount Eligible"
                />
              </FormGroup>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formState.exportToShopify ?? true}
                      onChange={handleCheckboxChange('exportToShopify')}
                    />
                  }
                  label="Export to Shopify"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formState.allowInventoryCreate ?? false}
                      onChange={handleCheckboxChange('allowInventoryCreate')}
                    />
                  }
                  label="Allow Inventory Create"
                />
              </FormGroup>
            </ConfigGrid>
            <FormControl fullWidth variant="outlined" margin="normal">
              <InputLabel id="qty-adjustment-direction-label">Qty Adjustment Direction</InputLabel>
              <Select
                labelId="qty-adjustment-direction-label"
                value={formState.quantityAdjustmentDirection || 'both'}
                onChange={e => handleChange('quantityAdjustmentDirection', e.target.value)}
                label="Quantity Adjustment Direction"
              >
                <MenuItem value="both">Both (increase and decrease)</MenuItem>
                <MenuItem value="increase">Increase only</MenuItem>
                <MenuItem value="decrease">Decrease only</MenuItem>
              </Select>
            </FormControl>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap={16} mt={2}>
              <TextField
                fullWidth
                variant="outlined"
                label="NetSuite Account ID"
                type="number"
                value={formState.netsuiteAccountId || ''}
                onChange={e => handleChange('netsuiteAccountId', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Account Name (optional)"
                value={formState.netsuiteAccountName || ''}
                onChange={e => handleChange('netsuiteAccountName', e.target.value)}
                margin="normal"
              />
            </Box>
          </Box>

          <Box mt={3} mb={2}>
            <Typography variant="h6" gutterBottom>
              Permissions
            </Typography>
            <FormControl fullWidth variant="outlined" margin="normal">
              <InputLabel id="permissions-select-label">Permissions</InputLabel>
              <Select
                labelId="permissions-select-label"
                id="permissions-select"
                multiple
                value={formState.permissions || []}
                onChange={e => handleChange('permissions', e.target.value)}
                renderValue={(selected: any) =>
                  (selected as number[])
                    .map(id => permissions.find(p => p.id === id)?.name)
                    .filter(Boolean)
                    .join(', ')
                }
                label="Permissions"
              >
                {permissions.map(permission => (
                  <MenuItem key={permission.id} value={permission.id}>
                    <Checkbox checked={(formState.permissions || []).includes(permission.id)} />
                    <ListItemText primary={permission.name.split('_').join(' ')} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {error && (
            <Box mt={2}>
              <Typography color="error">Error saving adjustment reason</Typography>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button ordinality="secondary" variant="text" onClick={toggleModal} disabled={loading}>
            Cancel
          </Button>
          <Button ordinality="primary" type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Submit'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AdjustmentReasonModal;
